import React, { useState, useEffect } from "react";
import { Icon } from "./Icon";
import { Routes, Route, Link, useLocation } from "react-router-dom";
import axios from "axios";

interface MetricSnapshot {
  timestamp: string;
  messagesProcessed: number;
  avgResponseTime: number;
  activeUsers: number;
  errorCount: number;
  apiCalls: {
    openai: number;
    openaiEmbeddings?: number;
    twitch?: number; // Keep for backward compatibility
    twitchOutgoing?: number;
    twitchIncoming?: number;
  };
  botStats?: {
    totalBots: number;
    activeBots: number;
    connectedBots?: number;
  };
}

interface UsageStatistics {
  daily: Array<{
    date: string;
    messagesTotal: number;
    messagesBot: number;
    messagesUser: number;
    uniqueUsers: number;
    avgResponseTime: number;
    peakHour: number;
    peakHourMessages: number;
  }>;
  monthly: Array<{
    month: string;
    messagesTotal: number;
    uniqueUsers: number;
    totalCost: number;
    avgDailyMessages: number;
  }>;
  realtime: {
    last5Minutes: MetricSnapshot;
    last1Hour: MetricSnapshot;
    last24Hours: MetricSnapshot;
  };
}

interface SystemHealth {
  status: "healthy" | "degraded" | "unhealthy";
  uptime: number;
  lastError?: {
    timestamp: string;
    message: string;
    count: number;
  };
  services: {
    database: "connected" | "disconnected";
    openai: "available" | "unavailable" | "rate_limited";
    twitch: "connected" | "disconnected";
  };
  resources: {
    memoryUsage: number;
    cpuUsage: number;
  };
}

interface RateLimit {
  service: string;
  used: number;
  limit: number;
  resetIn: number;
}

interface ActiveStream {
  channelName: string;
  botInstanceId: string;
  lastMessageTime: string;
  messageCount: number;
  isLive: boolean;
  viewerCount?: number;
}

interface ActiveStreamsResponse {
  active: ActiveStream[];
  recent: ActiveStream[];
}

interface MonitoringDashboardProps {
  authToken: string;
}

const MonitoringDashboard: React.FC<MonitoringDashboardProps> = ({
  authToken,
}) => {
  const [realtimeMetrics, setRealtimeMetrics] = useState<MetricSnapshot | null>(
    null,
  );
  const [usageStats, setUsageStats] = useState<UsageStatistics | null>(null);
  const [systemHealth, setSystemHealth] = useState<SystemHealth | null>(null);
  const [rateLimits, setRateLimits] = useState<RateLimit[]>([]);
  const [activeStreams, setActiveStreams] = useState<ActiveStream[]>([]);
  const [recentStreams, setRecentStreams] = useState<ActiveStream[]>([]);
  const [loading, setLoading] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 860);
    };
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  const getActiveTab = () => {
    const path = location.pathname;
    if (path.includes("usage")) return "usage";
    if (path.includes("health")) return "health";
    if (path.includes("streams")) return "streams";
    return "realtime";
  };

  const activeTab = getActiveTab();

  useEffect(() => {
    fetchMonitoringData();

    // Set up auto-refresh for real-time data
    const interval = setInterval(() => {
      if (activeTab === "realtime") {
        fetchRealtimeMetrics();
        fetchRateLimits(); // Also update rate limits
      } else if (activeTab === "health") {
        fetchSystemHealth();
      } else if (activeTab === "streams") {
        fetchActiveStreams();
      }
    }, 1000); // Refresh every second

    return () => clearInterval(interval);
  }, [activeTab]);

  const fetchMonitoringData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        fetchRealtimeMetrics(),
        fetchUsageStats(),
        fetchSystemHealth(),
        fetchRateLimits(),
        fetchActiveStreams(),
      ]);
    } catch (error) {
      console.error("Error fetching monitoring data:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchRealtimeMetrics = async () => {
    try {
      const response = await axios.get("/api/bot/monitoring/global-metrics", {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      setRealtimeMetrics(response.data);
    } catch (error) {
      console.error("Error fetching realtime metrics:", error);
    }
  };

  const fetchUsageStats = async () => {
    try {
      const response = await axios.get("/api/bot/monitoring/usage", {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      setUsageStats(response.data);
    } catch (error) {
      console.error("Error fetching usage stats:", error);
    }
  };

  const fetchSystemHealth = async () => {
    try {
      const response = await axios.get("/api/bot/monitoring/health", {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      setSystemHealth(response.data);
    } catch (error) {
      console.error("Error fetching system health:", error);
    }
  };

  const fetchRateLimits = async () => {
    try {
      const response = await axios.get("/api/bot/monitoring/rate-limits", {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      setRateLimits(response.data);
    } catch (error) {
      console.error("Error fetching rate limits:", error);
    }
  };

  const fetchActiveStreams = async () => {
    try {
      const response = await axios.get<ActiveStreamsResponse>(
        "/api/bot/monitoring/active-streams",
        {
          headers: { Authorization: `Bearer ${authToken}` },
        },
      );
      setActiveStreams(response.data.active);
      setRecentStreams(response.data.recent);
    } catch (error) {
      console.error("Error fetching active streams:", error);
    }
  };

  const formatUptime = (milliseconds: number) => {
    const seconds = Math.floor(milliseconds / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);

    if (days > 0) return `${days}d ${hours % 24}h`;
    if (hours > 0) return `${hours}h ${minutes % 60}m`;
    if (minutes > 0) return `${minutes}m ${seconds % 60}s`;
    return `${seconds}s`;
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "healthy":
      case "connected":
      case "available":
        return "#10B981";
      case "degraded":
      case "rate_limited":
        return "#F59E0B";
      case "unhealthy":
      case "disconnected":
      case "unavailable":
        return "#EF4444";
      default:
        return "#6B7280";
    }
  };

  const renderMetricCard = (
    title: string,
    value: string | number,
    unit?: string,
    color?: string,
  ) => (
    <div
      style={{
        backgroundColor: "#2d3748",
        padding: "20px",
        borderRadius: "8px",
        boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
      }}
    >
      <div
        style={{
          fontSize: isMobile ? "12px" : "14px",
          color: "#cbd5e0",
          marginBottom: "8px",
        }}
      >
        {title}
      </div>
      <div
        style={{
          fontSize: isMobile ? "20px" : "28px",
          fontWeight: "bold",
          color: color || "#e2e8f0",
        }}
      >
        {value}
        {unit && (
          <span
            style={{ fontSize: isMobile ? "14px" : "18px", color: "#cbd5e0" }}
          >
            {" "}
            {unit}
          </span>
        )}
      </div>
    </div>
  );

  const renderDailyChart = () => {
    if (!usageStats?.daily.length) return null;

    const maxMessages = Math.max(
      ...usageStats.daily.map((d) => d.messagesTotal),
    );
    const chartHeight = 200;

    return (
      <div
        style={{
          backgroundColor: "#2d3748",
          padding: "20px",
          borderRadius: "8px",
          border: "1px solid #4a5568",
        }}
      >
        <h4 style={{ marginTop: 0, marginBottom: "20px", color: "#e2e8f0" }}>
          Daily Message Volume (Last 30 Days)
        </h4>
        <div
          style={{
            position: "relative",
            height: `${chartHeight}px`,
            marginBottom: "40px",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "flex-end",
              height: "100%",
              gap: "1px",
            }}
          >
            {usageStats.daily.map((day, idx) => {
              const height =
                maxMessages > 0 ? (day.messagesTotal / maxMessages) * 100 : 0;
              return (
                <div
                  key={idx}
                  style={{
                    flex: 1,
                    backgroundColor: "#9146FF",
                    height: `${height}%`,
                    borderRadius: "2px 2px 0 0",
                    opacity: 0.8,
                    transition: "opacity 0.2s",
                    cursor: "pointer",
                  }}
                  title={`${new Date(day.date).toLocaleDateString()}: ${
                    day.messagesTotal
                  } messages`}
                  onMouseEnter={(e) => (e.currentTarget.style.opacity = "1")}
                  onMouseLeave={(e) => (e.currentTarget.style.opacity = "0.8")}
                />
              );
            })}
          </div>
          <div
            style={{
              position: "absolute",
              bottom: "-30px",
              left: 0,
              right: 0,
              borderTop: "1px solid #4a5568",
              paddingTop: "8px",
              display: "flex",
              justifyContent: "space-between",
              fontSize: "12px",
              color: "#cbd5e0",
            }}
          >
            <span>
              {new Date(usageStats.daily[0].date).toLocaleDateString()}
            </span>
            <span>
              {new Date(
                usageStats.daily[usageStats.daily.length - 1].date,
              ).toLocaleDateString()}
            </span>
          </div>
        </div>
      </div>
    );
  };

  // Remove loading screen to prevent flash when switching pages

  return (
    <div
      style={{
        maxWidth: "1200px",
        margin: "0 auto",
        padding: "0 20px",
        width: "100%",
      }}
    >
      <div
        style={{
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
          justifyContent: "space-between",
          alignItems: isMobile ? "stretch" : "center",
          gap: isMobile ? "16px" : "0",
          marginBottom: "24px",
        }}
      >
        <h3 style={{ fontSize: "20px", margin: 0, color: "#e2e8f0" }}>
          System Monitoring
        </h3>

        {/* Tab Selector */}
        <div
          style={{
            display: "flex",
            gap: "8px",
          }}
        >
          <Link
            to="/dashboard/admin/monitoring"
            style={{
              padding: "6px 16px",
              border: "none",
              borderRadius: "8px",
              backgroundColor: activeTab === "realtime" ? "#9146FF" : "#2d3748",
              color: activeTab === "realtime" ? "white" : "#cbd5e0",
              cursor: "pointer",
              fontSize: "14px",
              fontWeight: activeTab === "realtime" ? "bold" : "normal",
              textDecoration: "none",
              display: "inline-block",
            }}
          >
            Real-time
          </Link>
          <Link
            to="/dashboard/admin/monitoring/streams"
            style={{
              padding: "6px 16px",
              border: "none",
              borderRadius: "8px",
              backgroundColor: activeTab === "streams" ? "#9146FF" : "#2d3748",
              color: activeTab === "streams" ? "white" : "#cbd5e0",
              cursor: "pointer",
              fontSize: "14px",
              fontWeight: activeTab === "streams" ? "bold" : "normal",
              textDecoration: "none",
              display: "inline-block",
            }}
          >
            Streams
          </Link>
          <Link
            to="/dashboard/admin/monitoring/usage"
            style={{
              padding: "6px 16px",
              border: "none",
              borderRadius: "8px",
              backgroundColor: activeTab === "usage" ? "#9146FF" : "#2d3748",
              color: activeTab === "usage" ? "white" : "#cbd5e0",
              cursor: "pointer",
              fontSize: "14px",
              fontWeight: activeTab === "usage" ? "bold" : "normal",
              textDecoration: "none",
              display: "inline-block",
            }}
          >
            Usage Stats
          </Link>
          <Link
            to="/dashboard/admin/monitoring/health"
            style={{
              padding: "6px 16px",
              border: "none",
              borderRadius: "8px",
              backgroundColor: activeTab === "health" ? "#9146FF" : "#2d3748",
              color: activeTab === "health" ? "white" : "#cbd5e0",
              cursor: "pointer",
              fontSize: "14px",
              fontWeight: activeTab === "health" ? "bold" : "normal",
              textDecoration: "none",
              display: "inline-block",
            }}
          >
            System Health
          </Link>
        </div>
      </div>

      <Routes>
        <Route
          path="/"
          element={
            activeTab === "realtime" && realtimeMetrics ? (
              <div style={{ display: "grid", gap: "20px" }}>
                {/* Real-time Metrics */}
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: isMobile
                      ? "repeat(2, 1fr)"
                      : "repeat(4, 1fr)",
                    gap: isMobile ? "12px" : "16px",
                  }}
                >
                  {renderMetricCard(
                    "Messages (5 min)",
                    realtimeMetrics.messagesProcessed.toString(),
                  )}
                  {renderMetricCard(
                    "Active Users",
                    realtimeMetrics.activeUsers.toString(),
                  )}
                  {renderMetricCard(
                    "Avg Response Time",
                    realtimeMetrics.avgResponseTime.toString(),
                    "ms",
                  )}
                  {renderMetricCard(
                    "Errors",
                    realtimeMetrics.errorCount.toString(),
                    "",
                    realtimeMetrics.errorCount > 0 ? "#EF4444" : undefined,
                  )}
                </div>

                {/* Bot Statistics */}
                {realtimeMetrics.botStats && (
                  <div
                    style={{
                      backgroundColor: "#2d3748",
                      padding: "20px",
                      borderRadius: "8px",
                      border: "1px solid #4a5568",
                    }}
                  >
                    <h4
                      style={{
                        marginTop: 0,
                        marginBottom: "16px",
                        color: "#e2e8f0",
                      }}
                    >
                      Bot Statistics
                    </h4>
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns:
                          realtimeMetrics.botStats.connectedBots !== undefined
                            ? "1fr 1fr 1fr"
                            : "1fr 1fr",
                        gap: "16px",
                      }}
                    >
                      {renderMetricCard(
                        "Total Bots",
                        realtimeMetrics.botStats.totalBots.toString(),
                        "",
                        "#e2e8f0",
                      )}
                      {renderMetricCard(
                        "Active Bots",
                        realtimeMetrics.botStats.activeBots.toString(),
                        "",
                        "#22C55E",
                      )}
                      {realtimeMetrics.botStats.connectedBots !== undefined &&
                        renderMetricCard(
                          "Connected Bots",
                          realtimeMetrics.botStats.connectedBots.toString(),
                          "",
                          "#3B82F6",
                        )}
                    </div>
                  </div>
                )}

                {/* API Usage */}
                <div
                  style={{
                    backgroundColor: "#2d3748",
                    padding: "20px",
                    borderRadius: "8px",
                    border: "1px solid #4a5568",
                  }}
                >
                  <h4
                    style={{
                      marginTop: 0,
                      marginBottom: "16px",
                      color: "#e2e8f0",
                    }}
                  >
                    API Usage
                  </h4>
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: isMobile
                        ? "1fr 1fr"
                        : "repeat(4, 1fr)",
                      gap: "16px",
                    }}
                  >
                    <div>
                      <div
                        style={{
                          fontSize: "12px",
                          color: "#cbd5e0",
                          marginBottom: "4px",
                        }}
                      >
                        OpenAI Chat
                      </div>
                      <div
                        style={{
                          fontSize: "20px",
                          fontWeight: "bold",
                          color: "#e2e8f0",
                        }}
                      >
                        {realtimeMetrics.apiCalls.openai}
                      </div>
                    </div>
                    <div>
                      <div
                        style={{
                          fontSize: "12px",
                          color: "#cbd5e0",
                          marginBottom: "4px",
                        }}
                      >
                        OpenAI Embeddings
                      </div>
                      <div
                        style={{
                          fontSize: "20px",
                          fontWeight: "bold",
                          color: "#e2e8f0",
                        }}
                      >
                        {realtimeMetrics.apiCalls.openaiEmbeddings || 0}
                      </div>
                    </div>
                    <div>
                      <div
                        style={{
                          fontSize: "12px",
                          color: "#cbd5e0",
                          marginBottom: "4px",
                        }}
                      >
                        Twitch Outgoing
                      </div>
                      <div
                        style={{
                          fontSize: "20px",
                          fontWeight: "bold",
                          color: "#e2e8f0",
                        }}
                      >
                        {realtimeMetrics.apiCalls.twitchOutgoing ||
                          realtimeMetrics.apiCalls.twitch ||
                          0}
                      </div>
                    </div>
                    <div>
                      <div
                        style={{
                          fontSize: "12px",
                          color: "#cbd5e0",
                          marginBottom: "4px",
                        }}
                      >
                        Twitch Incoming
                      </div>
                      <div
                        style={{
                          fontSize: "20px",
                          fontWeight: "bold",
                          color: "#10B981",
                        }}
                      >
                        {realtimeMetrics.apiCalls.twitchIncoming || 0}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Rate Limits */}
                {rateLimits.length > 0 && (
                  <div
                    style={{
                      backgroundColor: "#2d3748",
                      padding: "20px",
                      borderRadius: "8px",
                      border: "1px solid #4a5568",
                    }}
                  >
                    <h4
                      style={{
                        marginTop: 0,
                        marginBottom: "16px",
                        color: "#e2e8f0",
                      }}
                    >
                      Rate Limits
                    </h4>
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "12px",
                      }}
                    >
                      {rateLimits.map((limit) => {
                        const percentUsed = (limit.used / limit.limit) * 100;
                        const color =
                          percentUsed > 90
                            ? "#EF4444"
                            : percentUsed > 70
                              ? "#F59E0B"
                              : "#10B981";

                        return (
                          <div key={limit.service}>
                            <div
                              style={{
                                display: "flex",
                                justifyContent: "space-between",
                                marginBottom: "4px",
                              }}
                            >
                              <span
                                style={{
                                  fontWeight: "500",
                                  textTransform: "capitalize",
                                  color: "#e2e8f0",
                                }}
                              >
                                {limit.service}
                              </span>
                              <span
                                style={{ fontSize: "14px", color: "#cbd5e0" }}
                              >
                                {limit.used} / {limit.limit} (resets in{" "}
                                {Math.ceil(limit.resetIn / 1000)}
                                s)
                              </span>
                            </div>
                            <div
                              style={{
                                height: "8px",
                                backgroundColor: "#4a5568",
                                borderRadius: "4px",
                                overflow: "hidden",
                              }}
                            >
                              <div
                                style={{
                                  width: `${percentUsed}%`,
                                  height: "100%",
                                  backgroundColor: color,
                                  transition: "width 0.3s ease",
                                }}
                              />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            ) : null
          }
        />
        <Route
          path="usage"
          element={
            activeTab === "usage" && usageStats ? (
              <div style={{ display: "grid", gap: "20px" }}>
                {/* Monthly Summary */}
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: isMobile ? "1fr" : "repeat(3, 1fr)",
                    gap: "16px",
                  }}
                >
                  {usageStats.monthly.map((month) => (
                    <div
                      key={month.month}
                      style={{
                        backgroundColor: "#2d3748",
                        padding: "20px",
                        borderRadius: "8px",
                        border: "1px solid #4a5568",
                      }}
                    >
                      <div
                        style={{
                          fontSize: "16px",
                          fontWeight: "600",
                          marginBottom: "12px",
                          color: "#9146FF",
                        }}
                      >
                        {month.month}
                      </div>
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: "8px",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                          }}
                        >
                          <span style={{ color: "#cbd5e0", fontSize: "14px" }}>
                            Total Messages
                          </span>
                          <span
                            style={{
                              fontWeight: "500",
                              fontSize: "14px",
                              color: "#e2e8f0",
                            }}
                          >
                            {month.messagesTotal.toLocaleString()}
                          </span>
                        </div>
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                          }}
                        >
                          <span style={{ color: "#cbd5e0", fontSize: "14px" }}>
                            Unique Users
                          </span>
                          <span
                            style={{
                              fontWeight: "500",
                              fontSize: "14px",
                              color: "#e2e8f0",
                            }}
                          >
                            {month.uniqueUsers}
                          </span>
                        </div>
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                          }}
                        >
                          <span style={{ color: "#cbd5e0", fontSize: "14px" }}>
                            Daily Average
                          </span>
                          <span
                            style={{
                              fontWeight: "500",
                              fontSize: "14px",
                              color: "#e2e8f0",
                            }}
                          >
                            {month.avgDailyMessages}
                          </span>
                        </div>
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            paddingTop: "8px",
                            borderTop: "1px solid #4a5568",
                          }}
                        >
                          <span style={{ color: "#cbd5e0", fontSize: "14px" }}>
                            Est. Cost
                          </span>
                          <span
                            style={{
                              fontWeight: "600",
                              color: "#059669",
                              fontSize: "14px",
                            }}
                          >
                            ${month.totalCost}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Daily Chart */}
                {renderDailyChart()}

                {/* Usage Summary */}
                <div
                  style={{
                    backgroundColor: "#2d3748",
                    padding: "20px",
                    borderRadius: "8px",
                    border: "1px solid #4a5568",
                  }}
                >
                  <h4
                    style={{
                      marginTop: 0,
                      marginBottom: "16px",
                      color: "#e2e8f0",
                    }}
                  >
                    Usage Summary
                  </h4>
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr 1fr",
                      gap: isMobile ? "16px" : "24px",
                    }}
                  >
                    <div>
                      <div
                        style={{
                          fontSize: "14px",
                          color: "#cbd5e0",
                          marginBottom: "8px",
                        }}
                      >
                        Last 5 Minutes
                      </div>
                      <div
                        style={{
                          fontSize: "18px",
                          fontWeight: "bold",
                          color: "#e2e8f0",
                        }}
                      >
                        {usageStats.realtime.last5Minutes.messagesProcessed}{" "}
                        messages
                      </div>
                      <div style={{ fontSize: "14px", color: "#cbd5e0" }}>
                        {usageStats.realtime.last5Minutes.activeUsers} active
                        users
                      </div>
                    </div>
                    <div>
                      <div
                        style={{
                          fontSize: "14px",
                          color: "#cbd5e0",
                          marginBottom: "8px",
                        }}
                      >
                        Last Hour
                      </div>
                      <div
                        style={{
                          fontSize: "18px",
                          fontWeight: "bold",
                          color: "#e2e8f0",
                        }}
                      >
                        {usageStats.realtime.last1Hour.messagesProcessed}{" "}
                        messages
                      </div>
                      <div style={{ fontSize: "14px", color: "#cbd5e0" }}>
                        {usageStats.realtime.last1Hour.activeUsers} active users
                      </div>
                    </div>
                    <div>
                      <div
                        style={{
                          fontSize: "14px",
                          color: "#cbd5e0",
                          marginBottom: "8px",
                        }}
                      >
                        Last 24 Hours
                      </div>
                      <div
                        style={{
                          fontSize: "18px",
                          fontWeight: "bold",
                          color: "#e2e8f0",
                        }}
                      >
                        {usageStats.realtime.last24Hours.messagesProcessed}{" "}
                        messages
                      </div>
                      <div style={{ fontSize: "14px", color: "#cbd5e0" }}>
                        {usageStats.realtime.last24Hours.activeUsers} active
                        users
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ) : null
          }
        />
        <Route
          path="health"
          element={
            activeTab === "health" && systemHealth ? (
              <div style={{ display: "grid", gap: "20px" }}>
                {/* Overall Health Status */}
                <div
                  style={{
                    backgroundColor: "#2d3748",
                    padding: "20px",
                    borderRadius: "8px",
                    borderLeft: `4px solid ${getStatusColor(systemHealth.status)}`,
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "16px",
                    }}
                  >
                    <div
                      style={{
                        width: "12px",
                        height: "12px",
                        borderRadius: "50%",
                        backgroundColor: getStatusColor(systemHealth.status),
                      }}
                    />
                    <div>
                      <div
                        style={{
                          fontSize: "18px",
                          fontWeight: "600",
                          textTransform: "capitalize",
                          color: "#e2e8f0",
                        }}
                      >
                        System {systemHealth.status}
                      </div>
                      <div style={{ fontSize: "14px", color: "#cbd5e0" }}>
                        Uptime: {formatUptime(systemHealth.uptime)}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Services Status */}
                <div
                  style={{
                    backgroundColor: "#2d3748",
                    padding: "20px",
                    borderRadius: "8px",
                    border: "1px solid #4a5568",
                  }}
                >
                  <h4
                    style={{
                      marginTop: 0,
                      marginBottom: "16px",
                      color: "#e2e8f0",
                    }}
                  >
                    Service Status
                  </h4>
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: isMobile ? "1fr" : "repeat(3, 1fr)",
                      gap: "16px",
                    }}
                  >
                    {Object.entries(systemHealth.services).map(
                      ([service, status]) => (
                        <div
                          key={service}
                          style={{
                            padding: "16px",
                            backgroundColor: "#374151",
                            borderRadius: "8px",
                            display: "flex",
                            alignItems: "center",
                            gap: "12px",
                          }}
                        >
                          <div
                            style={{
                              width: "8px",
                              height: "8px",
                              borderRadius: "50%",
                              backgroundColor: getStatusColor(status),
                            }}
                          />
                          <div>
                            <div
                              style={{
                                fontWeight: "500",
                                textTransform: "capitalize",
                                color: "#e2e8f0",
                              }}
                            >
                              {service}
                            </div>
                            <div
                              style={{
                                fontSize: "14px",
                                color: getStatusColor(status),
                              }}
                            >
                              {status.replace("_", " ")}
                            </div>
                          </div>
                        </div>
                      ),
                    )}
                  </div>
                </div>

                {/* Resource Usage */}
                <div
                  style={{
                    backgroundColor: "#2d3748",
                    padding: "20px",
                    borderRadius: "8px",
                    border: "1px solid #4a5568",
                  }}
                >
                  <h4
                    style={{
                      marginTop: 0,
                      marginBottom: "16px",
                      color: "#e2e8f0",
                    }}
                  >
                    Resource Usage
                  </h4>
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
                      gap: isMobile ? "16px" : "24px",
                    }}
                  >
                    <div>
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          marginBottom: "8px",
                        }}
                      >
                        <span style={{ fontWeight: "500", color: "#e2e8f0" }}>
                          Memory Usage
                        </span>
                        <span style={{ fontSize: "14px", color: "#cbd5e0" }}>
                          {systemHealth.resources.memoryUsage}%
                        </span>
                      </div>
                      <div
                        style={{
                          height: "8px",
                          backgroundColor: "#4a5568",
                          borderRadius: "4px",
                          overflow: "hidden",
                        }}
                      >
                        <div
                          style={{
                            width: `${systemHealth.resources.memoryUsage}%`,
                            height: "100%",
                            backgroundColor:
                              systemHealth.resources.memoryUsage > 90
                                ? "#EF4444"
                                : "#10B981",
                            transition: "width 0.3s ease",
                          }}
                        />
                      </div>
                    </div>
                    <div>
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          marginBottom: "8px",
                        }}
                      >
                        <span style={{ fontWeight: "500", color: "#e2e8f0" }}>
                          CPU Usage
                        </span>
                        <span style={{ fontSize: "14px", color: "#cbd5e0" }}>
                          {systemHealth.resources.cpuUsage}%
                        </span>
                      </div>
                      <div
                        style={{
                          height: "8px",
                          backgroundColor: "#4a5568",
                          borderRadius: "4px",
                          overflow: "hidden",
                        }}
                      >
                        <div
                          style={{
                            width: `${systemHealth.resources.cpuUsage}%`,
                            height: "100%",
                            backgroundColor:
                              systemHealth.resources.cpuUsage > 80
                                ? "#EF4444"
                                : "#10B981",
                            transition: "width 0.3s ease",
                          }}
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Last Error */}
                {systemHealth.lastError && (
                  <div
                    style={{
                      backgroundColor: "#2d3748",
                      padding: "20px",
                      borderRadius: "8px",
                      border: "1px solid #EF4444",
                    }}
                  >
                    <h4
                      style={{
                        marginTop: 0,
                        marginBottom: "12px",
                        color: "#EF4444",
                      }}
                    >
                      Recent Errors
                    </h4>
                    <div style={{ fontSize: "14px", color: "#fecaca" }}>
                      <div style={{ marginBottom: "4px" }}>
                        <strong>Last Error:</strong>{" "}
                        {systemHealth.lastError.message}
                      </div>
                      <div style={{ display: "flex", gap: "24px" }}>
                        <span>
                          <strong>Time:</strong>{" "}
                          {new Date(
                            systemHealth.lastError.timestamp,
                          ).toLocaleString()}
                        </span>
                        <span>
                          <strong>Count (last hour):</strong>{" "}
                          {systemHealth.lastError.count}
                        </span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ) : null
          }
        />
        <Route
          path="streams"
          element={
            activeTab === "streams" && !loading ? (
              <div style={{ display: "grid", gap: "20px" }}>
                <div
                  style={{
                    backgroundColor: "#2d3748",
                    padding: "20px",
                    borderRadius: "8px",
                    border: "1px solid #4a5568",
                  }}
                >
                  <h4
                    style={{
                      marginTop: 0,
                      marginBottom: "20px",
                      color: "#e2e8f0",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <span>Active Streams (Last 10 Minutes)</span>
                    <span style={{ fontSize: "14px", color: "#cbd5e0" }}>
                      {activeStreams?.length || 0} streams
                    </span>
                  </h4>

                  {!activeStreams || activeStreams.length === 0 ? (
                    <div
                      style={{
                        textAlign: "center",
                        padding: "40px",
                        color: "#cbd5e0",
                      }}
                    >
                      No active streams in the last 10 minutes
                    </div>
                  ) : (
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "12px",
                      }}
                    >
                      {activeStreams.map((stream, idx) => (
                        <div
                          key={stream.botInstanceId}
                          style={{
                            backgroundColor: "#374151",
                            padding: "16px",
                            borderRadius: "8px",
                            display: "grid",
                            gridTemplateColumns: isMobile
                              ? "1fr"
                              : "2fr 1fr 1fr 1fr 1fr",
                            gap: isMobile ? "8px" : "16px",
                            alignItems: "center",
                          }}
                        >
                          <div>
                            <div
                              style={{
                                fontSize: "16px",
                                fontWeight: "600",
                                color: "#e2e8f0",
                                display: "flex",
                                alignItems: "center",
                                gap: "8px",
                              }}
                            >
                              <span>#{idx + 1}</span>
                              <span>{stream.channelName}</span>
                              {stream.isLive && (
                                <span
                                  style={{
                                    backgroundColor: "#EF4444",
                                    color: "white",
                                    padding: "2px 8px",
                                    borderRadius: "4px",
                                    fontSize: "12px",
                                    fontWeight: "normal",
                                  }}
                                >
                                  LIVE
                                </span>
                              )}
                            </div>
                          </div>

                          <div
                            style={{ textAlign: isMobile ? "left" : "center" }}
                          >
                            <div style={{ fontSize: "12px", color: "#cbd5e0" }}>
                              Messages
                            </div>
                            <div
                              style={{
                                fontSize: "16px",
                                fontWeight: "500",
                                color: "#e2e8f0",
                              }}
                            >
                              {stream.messageCount}
                            </div>
                          </div>

                          <div
                            style={{ textAlign: isMobile ? "left" : "center" }}
                          >
                            <div style={{ fontSize: "12px", color: "#cbd5e0" }}>
                              Last Active
                            </div>
                            <div
                              style={{
                                fontSize: "14px",
                                color: "#e2e8f0",
                              }}
                            >
                              {new Date(
                                stream.lastMessageTime,
                              ).toLocaleTimeString()}
                            </div>
                          </div>

                          {stream.viewerCount !== undefined && (
                            <div
                              style={{
                                textAlign: isMobile ? "left" : "center",
                              }}
                            >
                              <div
                                style={{ fontSize: "12px", color: "#cbd5e0" }}
                              >
                                Viewers
                              </div>
                              <div
                                style={{
                                  fontSize: "16px",
                                  fontWeight: "500",
                                  color: "#10B981",
                                }}
                              >
                                {stream.viewerCount.toLocaleString()}
                              </div>
                            </div>
                          )}

                          <div
                            style={{ textAlign: isMobile ? "left" : "right" }}
                          >
                            <a
                              href={`https://twitch.tv/${stream.channelName}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{
                                color: "#9146FF",
                                textDecoration: "none",
                                fontSize: "14px",
                                fontWeight: "500",
                              }}
                            >
                              View Stream{" "}
                              <Icon
                                name="arrow-right"
                                size={14}
                                style={{
                                  verticalAlign: "-2px",
                                  marginLeft: "4px",
                                }}
                              />
                            </a>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Recent Streams Section */}
                {
                  <div
                    style={{
                      backgroundColor: "#2d3748",
                      padding: "20px",
                      borderRadius: "8px",
                      border: "1px solid #4a5568",
                      marginTop: "20px",
                    }}
                  >
                    <h4
                      style={{
                        marginTop: 0,
                        marginBottom: "20px",
                        color: "#e2e8f0",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <span>Recent Streams (Last 24 Hours)</span>
                      <span style={{ fontSize: "14px", color: "#cbd5e0" }}>
                        {recentStreams?.length || 0} streams
                      </span>
                    </h4>

                    {!recentStreams || recentStreams.length === 0 ? (
                      <div
                        style={{
                          textAlign: "center",
                          padding: "40px",
                          color: "#cbd5e0",
                        }}
                      >
                        No recent streams in the last 24 hours
                      </div>
                    ) : (
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: "12px",
                        }}
                      >
                        {recentStreams.map((stream, idx) => (
                          <div
                            key={stream.botInstanceId}
                            style={{
                              backgroundColor: "#374151",
                              padding: "16px",
                              borderRadius: "8px",
                              display: "grid",
                              gridTemplateColumns: isMobile
                                ? "1fr"
                                : "2fr 1fr 1fr 1fr",
                              gap: isMobile ? "8px" : "16px",
                              alignItems: "center",
                              opacity: 0.8,
                            }}
                          >
                            <div>
                              <div
                                style={{
                                  fontSize: "16px",
                                  fontWeight: "600",
                                  color: "#e2e8f0",
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "8px",
                                }}
                              >
                                <span style={{ color: "#9ca3af" }}>
                                  #{(activeStreams?.length || 0) + idx + 1}
                                </span>
                                <span>{stream.channelName}</span>
                                <span
                                  style={{
                                    backgroundColor: "#6B7280",
                                    color: "white",
                                    padding: "2px 8px",
                                    borderRadius: "4px",
                                    fontSize: "12px",
                                    fontWeight: "normal",
                                  }}
                                >
                                  OFFLINE
                                </span>
                              </div>
                            </div>

                            <div
                              style={{
                                textAlign: isMobile ? "left" : "center",
                              }}
                            >
                              <div
                                style={{ fontSize: "12px", color: "#cbd5e0" }}
                              >
                                Messages
                              </div>
                              <div
                                style={{
                                  fontSize: "16px",
                                  fontWeight: "500",
                                  color: "#e2e8f0",
                                }}
                              >
                                {stream.messageCount}
                              </div>
                            </div>

                            <div
                              style={{
                                textAlign: isMobile ? "left" : "center",
                              }}
                            >
                              <div
                                style={{ fontSize: "12px", color: "#cbd5e0" }}
                              >
                                Last Active
                              </div>
                              <div
                                style={{
                                  fontSize: "14px",
                                  color: "#e2e8f0",
                                }}
                              >
                                {new Date(
                                  stream.lastMessageTime,
                                ).toLocaleTimeString()}
                              </div>
                            </div>

                            <div
                              style={{ textAlign: isMobile ? "left" : "right" }}
                            >
                              <a
                                href={`https://twitch.tv/${stream.channelName}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{
                                  color: "#9146FF",
                                  textDecoration: "none",
                                  fontSize: "14px",
                                  fontWeight: "500",
                                }}
                              >
                                View Channel{" "}
                                <Icon
                                  name="arrow-right"
                                  size={14}
                                  style={{
                                    verticalAlign: "-2px",
                                    marginLeft: "4px",
                                  }}
                                />
                              </a>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                }
              </div>
            ) : activeTab === "streams" && loading ? (
              <div
                style={{
                  textAlign: "center",
                  padding: "40px",
                  color: "#cbd5e0",
                }}
              >
                Loading streams data...
              </div>
            ) : null
          }
        />
      </Routes>
    </div>
  );
};

export default MonitoringDashboard;
